import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  ArrowsClockwise,
  ShoppingBag,
  Trash,
  X,
} from "@phosphor-icons/react";
import ConfirmDialog from "./ConfirmDialog";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "quote_sent", label: "Quote Sent" },
  { value: "order_confirmed", label: "Order Confirmed" },
  { value: "fulfilled", label: "Fulfilled" },
  { value: "closed", label: "Closed" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const STATUS_PILL = {
  new: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  quote_sent: "bg-indigo-100 text-indigo-800",
  order_confirmed: "bg-emerald-100 text-emerald-800",
  fulfilled: "bg-green-100 text-green-800",
  closed: "bg-ink-200 text-ink-700",
};

const ORG_TYPE_LABELS = {
  hospice: "Hospice",
  hospital_healthcare: "Hospital / Healthcare",
  senior_living: "Senior Living",
  board_and_care: "Board & Care",
  church_faith: "Church / Faith",
  business_employer: "Business / Employer",
  community_organization: "Community Org",
  school_education: "School / Education",
  individual: "Individual",
  other: "Other",
};
const CONTACT_METHOD_LABELS = {
  email: "Email",
  phone: "Phone",
  either: "Either email or phone",
};

const productsSummary = (products) => {
  if (!products || products.length === 0) return "—";
  if (products.length === 1) {
    const p = products[0];
    return `${p.quantity} × ${p.product}`;
  }
  const totalQty = products.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
  return `${products.length} items · ${totalQty} total`;
};

const displayName = (r) => {
  if (r.first_name || r.last_name) {
    return [r.first_name, r.last_name].filter(Boolean).join(" ");
  }
  return r.name || "—";
};

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const Th = ({ children, className = "" }) => (
  <th
    className={`px-4 py-3 text-left text-[10px] uppercase tracking-[0.15em] font-semibold ${className}`}
  >
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

const StatCard = ({ label, value, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-ink-100 bg-white px-4 py-3.5"
  >
    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-500 font-semibold">
      {label}
    </div>
    <div className="mt-1 font-heading text-2xl text-ink-900">{value ?? "—"}</div>
  </div>
);

const BulkOrdersAdmin = ({ onDataChange }) => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refreshStats = useCallback(() => {
    axios
      .get(`${API_BASE}/admin/bulk-orders/stats`, { withCredentials: true })
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.status = status;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (q.trim()) params.q = q.trim();
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/admin/bulk-orders`, { params, withCredentials: true }),
        axios.get(`${API_BASE}/admin/bulk-orders/stats`, { withCredentials: true }),
      ]);
      setItems(listRes.data || []);
      setStats(statsRes.data);
    } catch {
      toast.error("Could not load bulk orders.");
    } finally {
      setLoading(false);
    }
  }, [status, fromDate, toDate, q]);

  useEffect(() => {
    load();
  }, [status, fromDate, toDate, load]);

  // Prune selections whenever the visible list changes so hidden rows can
  // never be silently deleted.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(items.map((i) => i.id));
      const next = new Set();
      let changed = false;
      prev.forEach((id) => {
        if (visible.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [items]);

  const updateSelected = async (patch) => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.patch(
        `${API_BASE}/admin/bulk-orders/${selected.id}`,
        patch,
        { withCredentials: true }
      );
      setSelected(res.data);
      setItems((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)));
      refreshStats();
      onDataChange?.();
      toast.success("Updated.");
    } catch {
      toast.error("Could not save the change.");
    } finally {
      setSaving(false);
    }
  };

  const toggleRow = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected =
    items.length > 0 && items.every((i) => selectedIds.has(i.id));
  const toggleAllVisible = () => {
    if (allVisibleSelected) clearSelection();
    else setSelectedIds(new Set(items.map((i) => i.id)));
  };

  const askDeleteSingle = (id) => setConfirmDelete({ mode: "single", ids: [id] });
  const askDeleteBulk = () => {
    if (selectedIds.size === 0) return;
    setConfirmDelete({ mode: "bulk", ids: Array.from(selectedIds) });
  };
  const cancelDelete = () => setConfirmDelete(null);

  const doDelete = async () => {
    if (!confirmDelete) return;
    const { mode, ids } = confirmDelete;
    setDeleting(true);
    try {
      if (mode === "single") {
        await axios.delete(
          `${API_BASE}/admin/bulk-orders/${ids[0]}`,
          { withCredentials: true }
        );
        setItems((prev) => prev.filter((r) => r.id !== ids[0]));
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(ids[0]);
          return next;
        });
        if (selected?.id === ids[0]) setSelected(null);
        toast.success("Bulk order deleted");
      } else {
        const res = await axios.post(
          `${API_BASE}/admin/bulk-orders/bulk-delete`,
          { ids },
          { withCredentials: true }
        );
        const removed = new Set(ids);
        setItems((prev) => prev.filter((r) => !removed.has(r.id)));
        clearSelection();
        const n = res.data?.deleted ?? ids.length;
        toast.success(`${n} bulk order${n === 1 ? "" : "s"} deleted`);
      }
      refreshStats();
      onDataChange?.();
      setConfirmDelete(null);
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        toast.error("Session ended. Please sign in again.");
      } else if (err?.response?.status === 404) {
        toast.error("Record no longer exists. Refreshing list…");
        load();
        setConfirmDelete(null);
      } else {
        toast.error("Could not delete. Please try again.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const preview = useMemo(
    () =>
      items.map((r) => ({
        ...r,
        message_preview:
          r.message && r.message.length > 120
            ? r.message.slice(0, 120).trim() + "…"
            : r.message || "",
      })),
    [items]
  );

  return (
    <section className="space-y-6" data-testid="bulk-orders-admin">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShoppingBag size={22} weight="duotone" />
        </span>
        <div>
          <h2 className="font-heading text-2xl text-ink-900 font-medium leading-tight">
            Bulk Orders
          </h2>
          <p className="mt-1 text-sm text-ink-600 max-w-2xl">
            Bulk-order inquiries submitted through the public site's{" "}
            <span className="font-semibold">Request a Bulk Order Quote</span>{" "}
            CTA. These are managed here and are hidden from the general Inbox.
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats?.total} testId="bo-stat-total" />
        <StatCard label="New" value={stats?.by_status?.new} testId="bo-stat-new" />
        <StatCard label="Contacted" value={stats?.by_status?.contacted} testId="bo-stat-contacted" />
        <StatCard label="Quote Sent" value={stats?.by_status?.quote_sent} testId="bo-stat-quote-sent" />
        <StatCard label="Confirmed" value={stats?.by_status?.order_confirmed} testId="bo-stat-order-confirmed" />
        <StatCard label="Fulfilled" value={stats?.by_status?.fulfilled} testId="bo-stat-fulfilled" />
      </div>

      {/* Filters */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          load();
        }}
        className="flex flex-wrap items-end gap-3"
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            Search
          </label>
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400"
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="bo-search"
              placeholder="Name, email, phone, subject, message…"
              className="w-full rounded-full border border-ink-200 bg-white pl-10 pr-3 py-2 text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            Status
          </label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="bo-filter-status"
            className="rounded-full border border-ink-200 bg-white px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            From
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="bo-filter-from"
            className="rounded-full border border-ink-200 bg-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
            To
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="bo-filter-to"
            className="rounded-full border border-ink-200 bg-white px-3 py-2 text-sm"
          />
        </div>
        <button
          type="submit"
          data-testid="bo-search-submit"
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-dark"
        >
          Apply
        </button>
        <button
          type="button"
          onClick={() => {
            setQ("");
            setStatus("all");
            setFromDate("");
            setToDate("");
          }}
          data-testid="bo-search-reset"
          className="inline-flex items-center gap-1 rounded-full border border-ink-200 bg-white px-3 py-2 text-xs font-semibold text-ink-600 hover:bg-ink-50"
        >
          <ArrowsClockwise size={12} weight="bold" />
          Reset
        </button>
      </form>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50/70 px-4 py-2.5"
          data-testid="bo-bulk-bar"
        >
          <div className="text-sm text-ink-800">
            <strong data-testid="bo-bulk-count">{selectedIds.size}</strong>{" "}
            selected
            <span className="text-ink-500"> (of {items.length} shown)</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={clearSelection}
              data-testid="bo-bulk-clear"
              className="rounded-full border border-ink-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:bg-ink-50"
            >
              Clear selection
            </button>
            <button
              type="button"
              onClick={askDeleteBulk}
              data-testid="bo-bulk-delete"
              className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"
            >
              <Trash size={12} weight="bold" />
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-ink-100 bg-white overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="bo-table">
          <thead className="bg-softblue/40 text-ink-700">
            <tr>
              <th className="px-3 py-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label="Select all visible bulk orders"
                  data-testid="bo-select-all"
                  className="h-4 w-4 accent-primary cursor-pointer"
                />
              </th>
              <Th>Date Received</Th>
              <Th>Contact</Th>
              <Th>Organization</Th>
              <Th>Type</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Products</Th>
              <Th>City / State</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {preview.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-ink-500 text-sm">
                  {loading ? "Loading..." : "No bulk-order requests match the current filters."}
                </td>
              </tr>
            ) : (
              preview.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`bo-row-${r.id}`}
                  onClick={() => setSelected(r)}
                  className={`border-t border-ink-100 hover:bg-softblue/20 cursor-pointer ${
                    selectedIds.has(r.id) ? "bg-red-50/40" : ""
                  }`}
                >
                  <td
                    className="px-3 py-3"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                      aria-label={`Select bulk order from ${displayName(r) || r.email || "unnamed"}`}
                      data-testid={`bo-select-${r.id}`}
                      className="h-4 w-4 accent-primary cursor-pointer"
                    />
                  </td>
                  <Td className="whitespace-nowrap text-ink-600 text-xs">{fmtDateTime(r.created_at)}</Td>
                  <Td className="font-semibold text-ink-900">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span>{displayName(r)}</span>
                      {r._source === "legacy" && (
                        <span
                          className="inline-flex items-center rounded-full bg-ink-100 text-ink-600 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                          title="Legacy request captured before the structured form was introduced"
                        >
                          Legacy
                        </span>
                      )}
                    </div>
                  </Td>
                  <Td className="text-ink-800">{r.organization_name || "—"}</Td>
                  <Td className="text-ink-700 text-xs">
                    {ORG_TYPE_LABELS[r.organization_type] || "—"}
                  </Td>
                  <Td className="text-ink-700">{r.email || "—"}</Td>
                  <Td className="text-ink-700 text-xs">{r.phone || "—"}</Td>
                  <Td className="text-ink-700 text-xs max-w-[220px]">
                    {productsSummary(r.products)}
                  </Td>
                  <Td className="text-ink-700 text-xs whitespace-nowrap">
                    {r.city && r.state ? `${r.city}, ${r.state}` : "—"}
                  </Td>
                  <Td>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${
                        STATUS_PILL[r.bulk_order_status] || STATUS_PILL.new
                      }`}
                    >
                      {STATUS_LABEL[r.bulk_order_status] || "New"}
                    </span>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <DetailDrawer
          bulkOrder={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateSelected}
          onDelete={() => askDeleteSingle(selected.id)}
          saving={saving}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title={
          confirmDelete?.mode === "bulk"
            ? `Delete ${confirmDelete.ids.length} selected bulk order${
                confirmDelete.ids.length === 1 ? "" : "s"
              }?`
            : "Delete this bulk order?"
        }
        description={
          confirmDelete?.mode === "bulk"
            ? `This will permanently remove ${confirmDelete.ids.length} bulk order${
                confirmDelete.ids.length === 1 ? "" : "s"
              } and cannot be undone.`
            : "This will permanently remove this bulk order. This action cannot be undone."
        }
        confirmLabel={
          confirmDelete?.mode === "bulk"
            ? `Delete ${confirmDelete.ids.length} bulk order${
                confirmDelete.ids.length === 1 ? "" : "s"
              }`
            : "Delete Bulk Order"
        }
        busy={deleting}
        onConfirm={doDelete}
        onCancel={cancelDelete}
        testId="bo-confirm-delete"
      />
    </section>
  );
};

const DetailDrawer = ({ bulkOrder: b, onClose, onUpdate, onDelete, saving }) => {
  const [notes, setNotes] = useState(b.internal_notes || "");
  useEffect(() => setNotes(b.internal_notes || ""), [b.id, b.internal_notes]);

  const isLegacy = b._source === "legacy";

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="bo-detail-drawer"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink-900/40"
      />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-ink-100 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg text-ink-900 font-medium">
              Bulk Order Details
            </h3>
            {isLegacy && (
              <span
                data-testid="bo-detail-legacy-pill"
                className="inline-flex items-center rounded-full bg-ink-100 text-ink-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              >
                Legacy
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-500 hover:bg-ink-100"
            data-testid="bo-detail-close"
            aria-label="Close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
              Received
            </div>
            <div className="text-ink-900">{fmtDateTime(b.created_at)}</div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
              Contact
            </div>
            <div className="text-ink-900 font-semibold">{displayName(b)}</div>
          </div>

          {b.organization_name && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Organization
              </div>
              <div className="text-ink-900">{b.organization_name}</div>
              {b.organization_type && (
                <div className="text-ink-500 text-xs mt-0.5">
                  {ORG_TYPE_LABELS[b.organization_type] || b.organization_type}
                </div>
              )}
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
              Email
            </div>
            <div className="text-ink-900">
              {b.email ? (
                <a href={`mailto:${b.email}`} className="text-primary hover:underline">
                  {b.email}
                </a>
              ) : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
              Phone
            </div>
            <div className="text-ink-900">
              {b.phone ? (
                <a href={`tel:${b.phone}`} className="text-primary hover:underline">
                  {b.phone}
                </a>
              ) : "—"}
            </div>
          </div>
          {b.preferred_contact && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Preferred contact
              </div>
              <div className="text-ink-800">
                {CONTACT_METHOD_LABELS[b.preferred_contact] || b.preferred_contact}
              </div>
            </div>
          )}

          {b.products && b.products.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
                Products requested
              </div>
              <ul className="rounded-xl border border-ink-100 divide-y divide-ink-100 bg-paper/60">
                {b.products.map((p, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                    data-testid={`bo-detail-product-${i}`}
                  >
                    <span className="text-ink-800">{p.product}</span>
                    <span className="font-semibold text-ink-900">× {p.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(b.city || b.state || b.zip_code) && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Location
              </div>
              <div className="text-ink-800">
                {[b.city, b.state, b.zip_code].filter(Boolean).join(", ") || "—"}
              </div>
            </div>
          )}
          {b.intended_use && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Intended use / audience
              </div>
              <div className="text-ink-800">{b.intended_use}</div>
            </div>
          )}
          {b.delivery_timeframe && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Delivery timeframe
              </div>
              <div className="text-ink-800">{b.delivery_timeframe}</div>
            </div>
          )}

          {/* Legacy-only fields */}
          {isLegacy && b.inquiry_type && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Inquiry Type
              </div>
              <div className="text-ink-700">{b.inquiry_type}</div>
            </div>
          )}
          {isLegacy && b.subject && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Subject
              </div>
              <div className="text-ink-900">{b.subject}</div>
            </div>
          )}

          {b.message && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Message
              </div>
              <p className="whitespace-pre-wrap text-ink-800 leading-relaxed border border-ink-100 rounded-xl p-3 bg-paper/60">
                {b.message}
              </p>
            </div>
          )}
          {b.source_page && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500">
                Source Page
              </div>
              <div className="text-ink-600 text-xs break-all">{b.source_page}</div>
            </div>
          )}

          <div className="border-t border-ink-100 pt-4">
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
              Bulk Order Status
            </div>
            <select
              value={b.bulk_order_status || "new"}
              onChange={(e) => onUpdate({ bulk_order_status: e.target.value })}
              disabled={saving}
              data-testid="bo-detail-status"
              className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-500 mb-1">
              Internal Notes (Admin-only)
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              data-testid="bo-detail-notes"
              className="w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm"
              placeholder="Private notes never shown to the customer"
            />
            <button
              type="button"
              onClick={() => onUpdate({ internal_notes: notes })}
              disabled={saving}
              data-testid="bo-detail-save-notes"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-xs font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
            >
              Save notes
            </button>
          </div>

          <div className="mt-4 border-t border-ink-100 pt-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-ink-500">
              Danger zone
            </div>
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              data-testid="bo-detail-delete"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash size={12} weight="bold" />
              Delete bulk order
            </button>
            <p className="mt-2 text-[11px] leading-snug text-ink-500">
              Use this only for test data, duplicates, spam, or clearly unwanted submissions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOrdersAdmin;
